import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell.jsx";
import MapboxFieldDraw from "../components/MapboxFieldDraw.jsx";
import { apiFetch, getStoredUser, safeReadError } from "../api/api.js";

/* =========================
   helpers
   ========================= */

function formatArea(v) {
  const n = Number(v || 0);
  return Number.isFinite(n) ? n.toFixed(1) : "0.0";
}

function shortText(value, max = 140) {
  const s = String(value || "").trim();
  if (!s) return "";
  if (s.length <= max) return s;
  return `${s.slice(0, max)}...`;
}

function parseMaybeJson(value) {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function getGeometryOnly(value) {
  const g = parseMaybeJson(value);
  if (!g) return null;
  if (g.type === "Feature" && g.geometry) return g.geometry;
  return g;
}

function getFieldGeometry(field) {
  return (
    getGeometryOnly(field?.geometry) ??
    getGeometryOnly(field?.geometryJson) ??
    getGeometryOnly(field?.contour) ??
    getGeometryOnly(field?.shape) ??
    getGeometryOnly(field?.polygon) ??
    getGeometryOnly(field?.coordinates) ??
    null
  );
}

function getContourPreview(field) {
  const geometry = getFieldGeometry(field);

  if (!geometry) return "Контур не додано";

  const type = String(geometry?.type || "").trim();

  if (type === "Polygon") {
    const rings = Array.isArray(geometry?.coordinates) ? geometry.coordinates : [];
    const points = Array.isArray(rings?.[0]) ? rings[0].length : 0;
    return `Polygon • точок: ${points || "—"}`;
  }

  if (type === "MultiPolygon") {
    const polygons = Array.isArray(geometry?.coordinates)
      ? geometry.coordinates.length
      : 0;
    return `MultiPolygon • полігонів: ${polygons || "—"}`;
  }

  try {
    return shortText(JSON.stringify(geometry), 120);
  } catch {
    return "Контур додано";
  }
}

function normalizeField(field, groupsMap) {
  const groupId =
    field?.groupId ??
    field?.fieldGroupId ??
    field?.group?.id ??
    field?.fieldGroup?.id ??
    null;

  const groupName =
    field?.groupName ||
    field?.group?.name ||
    field?.fieldGroup?.name ||
    groupsMap.get(Number(groupId))?.name ||
    "Без групи";

  const area =
    field?.areaHa ??
    field?.area ??
    field?.totalArea ??
    field?.hectares ??
    field?.size ??
    0;

  const note =
    field?.note ??
    field?.description ??
    field?.comment ??
    "";

  const name =
    field?.name ??
    field?.title ??
    `Поле #${field?.id ?? "—"}`;

  const geometry = getFieldGeometry(field);
  const contourExists = !!geometry;

  return {
    ...field,
    groupId: groupId == null ? null : Number(groupId),
    groupName,
    area: Number(area || 0),
    note: String(note || ""),
    name: String(name || ""),
    geometry,
    contourExists,
    contourPreview: getContourPreview(field),
  };
}

function flattenGeometryPoints(geometry) {
  if (!geometry) return [];

  if (geometry.type === "Polygon") {
    return Array.isArray(geometry.coordinates)
      ? geometry.coordinates.flat(1)
      : [];
  }

  if (geometry.type === "MultiPolygon") {
    return Array.isArray(geometry.coordinates)
      ? geometry.coordinates.flat(2)
      : [];
  }

  return [];
}

function geometryToSvgPath(geometry, width = 220, height = 140, padding = 10) {
  const points = flattenGeometryPoints(geometry);
  if (!points.length) return "";

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const p of points) {
    const x = Number(p?.[0]);
    const y = Number(p?.[1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;

    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }

  if (
    !Number.isFinite(minX) ||
    !Number.isFinite(minY) ||
    !Number.isFinite(maxX) ||
    !Number.isFinite(maxY)
  ) {
    return "";
  }

  const boxW = Math.max(1e-9, maxX - minX);
  const boxH = Math.max(1e-9, maxY - minY);

  const scaleX = (width - padding * 2) / boxW;
  const scaleY = (height - padding * 2) / boxH;
  const scale = Math.min(scaleX, scaleY);

  const drawW = boxW * scale;
  const drawH = boxH * scale;
  const offsetX = (width - drawW) / 2;
  const offsetY = (height - drawH) / 2;

  function projectPoint(pt) {
    const x = Number(pt?.[0]);
    const y = Number(pt?.[1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;

    const px = offsetX + (x - minX) * scale;
    const py = height - (offsetY + (y - minY) * scale);
    return [px, py];
  }

  function ringToPath(ring) {
    if (!Array.isArray(ring) || ring.length === 0) return "";
    const mapped = ring.map(projectPoint).filter(Boolean);
    if (!mapped.length) return "";
    const [first, ...rest] = mapped;
    return `M ${first[0].toFixed(2)} ${first[1].toFixed(2)} ${rest
      .map((p) => `L ${p[0].toFixed(2)} ${p[1].toFixed(2)}`)
      .join(" ")} Z`;
  }

  if (geometry.type === "Polygon") {
    const rings = Array.isArray(geometry.coordinates) ? geometry.coordinates : [];
    return rings.map(ringToPath).filter(Boolean).join(" ");
  }

  if (geometry.type === "MultiPolygon") {
    const polygons = Array.isArray(geometry.coordinates) ? geometry.coordinates : [];
    return polygons
      .flatMap((poly) => (Array.isArray(poly) ? poly.map(ringToPath) : []))
      .filter(Boolean)
      .join(" ");
  }

  return "";
}

function FieldThumb({ geometry }) {
  const path = useMemo(() => geometryToSvgPath(geometry, 64, 64, 5), [geometry]);

  if (!geometry || !path) {
    return (
      <div className="fg-fieldThumb fg-fieldThumb--empty">
        <span style={{ fontSize: 22, opacity: 0.55 }}>⛶</span>
      </div>
    );
  }

  return (
    <div className="fg-fieldThumb">
      <svg viewBox="0 0 64 64" className="fg-thumbSvg" aria-hidden="true">
        <rect x="0" y="0" width="64" height="64" className="fg-geoBg" />
        <path d={path} className="fg-geoShapeFill" />
        <path d={path} className="fg-geoShapeStroke" />
      </svg>
    </div>
  );
}

function GeometryPreview({ geometry }) {
  const path = useMemo(() => geometryToSvgPath(geometry), [geometry]);

  if (!geometry || !path) {
    return (
      <div className="fg-geoEmpty">
        <div className="fg-geoEmptyIcon">⛶</div>
        <div className="fg-geoEmptyText">Геометрія відсутня</div>
      </div>
    );
  }

  return (
    <div className="fg-geoCanvas">
      <svg
        className="fg-geoSvg"
        viewBox="0 0 220 140"
        preserveAspectRatio="xMidYMid meet"
        aria-label="Превʼю геометрії поля"
      >
        <defs>
          <pattern id="fgGrid" width="16" height="16" patternUnits="userSpaceOnUse">
            <path d="M 16 0 L 0 0 0 16" fill="none" className="fg-geoGridLine" />
          </pattern>
        </defs>

        <rect x="0" y="0" width="220" height="140" className="fg-geoBg" />
        <rect x="0" y="0" width="220" height="140" fill="url(#fgGrid)" />
        <path d={path} className="fg-geoShapeFill" />
        <path d={path} className="fg-geoShapeStroke" />
      </svg>
    </div>
  );
}

function getResponsiveMapHeight(width) {
  if (width <= 420) return 300;
  if (width <= 560) return 320;
  if (width <= 820) return 360;
  if (width <= 980) return 400;
  return 460;
}

export default function FieldGroups() {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [groups, setGroups] = useState([]);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);

  const [msg, setMsg] = useState(null);
  const [q, setQ] = useState("");
  const [groupFilter, setGroupFilter] = useState("all");
  const [viewMode, setViewMode] = useState("fields");

  const [groupCreateOpen, setGroupCreateOpen] = useState(false);
  const [fieldCreateOpen, setFieldCreateOpen] = useState(false);

  const [creatingGroup, setCreatingGroup] = useState(false);
  const [creatingField, setCreatingField] = useState(false);

  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupNote, setNewGroupNote] = useState("");

  const [newFieldName, setNewFieldName] = useState("");
  const [newFieldArea, setNewFieldArea] = useState("");
  const [newFieldNote, setNewFieldNote] = useState("");
  const [newFieldGroupId, setNewFieldGroupId] = useState("");
  const [polyFeature, setPolyFeature] = useState(null);

  const [mapHeight, setMapHeight] = useState(() =>
    typeof window === "undefined" ? 460 : getResponsiveMapHeight(window.innerWidth)
  );

  const [editGroupOpen,      setEditGroupOpen]      = useState(false);
  const [editGroup,          setEditGroup]          = useState(null);
  const [editGroupName,      setEditGroupName]      = useState("");
  const [editGroupNote,      setEditGroupNote]      = useState("");
  const [savingGroup,        setSavingGroup]        = useState(false);
  const [confirmDeleteGroup, setConfirmDeleteGroup] = useState(null);
  const [deletingGroup,      setDeletingGroup]      = useState(false);

  useEffect(() => {
    const u = getStoredUser();
    if (!u) {
      navigate("/login");
      return;
    }
    setUser(u);
  }, [navigate]);

  useEffect(() => {
    function handleResize() {
      setMapHeight(getResponsiveMapHeight(window.innerWidth));
    }

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  function toast(type, text) {
    setMsg({ type, text });
    window.clearTimeout(toast._t);
    toast._t = window.setTimeout(() => setMsg(null), 2600);
  }

  async function load() {
    if (!user) return;

    setLoading(true);
    try {
      const [resGroups, resFields] = await Promise.all([
        apiFetch(user, "/api/field-groups", { method: "GET" }, navigate),
        apiFetch(user, "/api/fields", { method: "GET" }, navigate),
      ]);

      let groupsArr = [];
      let fieldsArr = [];

      if (resGroups.ok) {
        const gj = await resGroups.json().catch(() => []);
        groupsArr = Array.isArray(gj) ? gj : [];
      } else {
        const e = await safeReadError(resGroups);
        toast("err", e || `Помилка груп (${resGroups.status})`);
      }

      if (resFields.ok) {
        const fj = await resFields.json().catch(() => []);
        fieldsArr = Array.isArray(fj) ? fj : [];
      } else {
        const e = await safeReadError(resFields);
        toast("err", e || `Помилка полів (${resFields.status})`);
      }

      const groupsMap = new Map(groupsArr.map((g) => [Number(g?.id), g]));
      const normalized = fieldsArr.map((f) => normalizeField(f, groupsMap));

      setGroups(groupsArr);
      setFields(normalized);

      if (!newFieldGroupId && groupsArr.length > 0) {
        setNewFieldGroupId(String(groupsArr[0].id));
      }
    } catch (e) {
      toast("err", e?.message || "Помилка завантаження");
      setGroups([]);
      setFields([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!user) return;
    load().catch(() => {});
  }, [user]);

  const filteredFields = useMemo(() => {
    const s = q.trim().toLowerCase();

    return fields.filter((f) => {
      const matchGroup =
        groupFilter === "all"
          ? true
          : groupFilter === "none"
          ? f.groupId == null
          : Number(f.groupId) === Number(groupFilter);

      if (!matchGroup) return false;
      if (!s) return true;

      const haystack = [
        f.name,
        f.note,
        f.groupName,
        f.contourPreview,
        String(f.id ?? ""),
        f.contourExists ? "є контур" : "нема контуру",
        f.geometry?.type || "",
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(s);
    });
  }, [fields, q, groupFilter]);

  const filteredGroups = useMemo(() => {
    const s = q.trim().toLowerCase();

    const arr = groups.map((g) => {
      const relatedFields = fields.filter((f) => Number(f.groupId) === Number(g.id));
      const area = relatedFields.reduce((acc, f) => acc + Number(f.area || 0), 0);
      const contourCount = relatedFields.filter((f) => f.contourExists).length;

      return {
        ...g,
        fieldsCount: relatedFields.length,
        area,
        contourCount,
      };
    });

    if (!s) {
      return arr.sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""), "uk")
      );
    }

    return arr
      .filter((g) =>
        [g.name, g.note, String(g.id), String(g.fieldsCount)]
          .join(" ")
          .toLowerCase()
          .includes(s)
      )
      .sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""), "uk")
      );
  }, [groups, fields, q]);

  const totals = useMemo(() => {
    const visibleArea = filteredFields.reduce(
      (acc, f) => acc + Number(f.area || 0),
      0
    );

    const contourCount = filteredFields.filter((f) => f.contourExists).length;

    return {
      totalFields: fields.length,
      visibleFields: filteredFields.length,
      visibleArea,
      contourCount,
    };
  }, [fields, filteredFields]);

  const groupStats = useMemo(() => {
    const map = new Map();

    for (const f of fields) {
      const key = f.groupId == null ? "none" : String(f.groupId);
      const current = map.get(key) || { count: 0, area: 0 };
      current.count += 1;
      current.area += Number(f.area || 0);
      map.set(key, current);
    }

    return map;
  }, [fields]);

  const activeGroupTitle = useMemo(() => {
    if (groupFilter === "all") return "Усі поля";
    if (groupFilter === "none") return "Без групи";
    return groups.find((g) => String(g.id) === String(groupFilter))?.name || "—";
  }, [groupFilter, groups]);

  function resetFieldForm(preferredGroupId = "") {
    setNewFieldName("");
    setNewFieldArea("");
    setNewFieldNote("");
    setPolyFeature(null);
    setNewFieldGroupId(
      preferredGroupId || (groups[0]?.id != null ? String(groups[0].id) : "")
    );
  }

  function openCreateGroup() {
    setNewGroupName("");
    setNewGroupNote("");
    setGroupCreateOpen(true);
  }

  function openCreateField(preferredGroupId = "") {
    resetFieldForm(preferredGroupId);
    setFieldCreateOpen(true);
  }

  function isPolygonFeature(x) {
    return !!(x && x.type === "Feature" && x.geometry && x.geometry.type === "Polygon");
  }

  async function handleCreateGroup(e) {
    e.preventDefault();
    if (!user || creatingGroup) return;

    const name = String(newGroupName || "").trim();
    const note = String(newGroupNote || "").trim();

    if (!name) {
      toast("err", "Введи назву групи");
      return;
    }

    setCreatingGroup(true);
    try {
      const payload = {
        name,
        note: note || null,
      };

      const res = await apiFetch(
        user,
        "/api/field-groups",
        { method: "POST", body: JSON.stringify(payload) },
        navigate
      );

      if (!res.ok) {
        const t = await safeReadError(res);
        toast("err", t || `Не вдалося створити групу (${res.status})`);
        return;
      }

      const created = await res.json().catch(() => null);
      const createdId = created?.id != null ? String(created.id) : "";

      setGroupCreateOpen(false);
      toast("ok", "Групу полів створено ✅");
      await load();

      if (createdId) {
        setViewMode("groups");
        setGroupFilter(createdId);
        setNewFieldGroupId(createdId);
      }
    } finally {
      setCreatingGroup(false);
    }
  }

  async function handleCreateField(e) {
    e.preventDefault();
    if (!user || creatingField) return;

    const name = String(newFieldName || "").trim();
    const area = Number(String(newFieldArea).replace(",", "."));
    const note = String(newFieldNote || "").trim();
    const fieldGroupId = Number(newFieldGroupId);

    if (!name || name.length < 2) {
      toast("err", "Назва поля мінімум 2 символи");
      return;
    }

    if (!Number.isFinite(area) || area <= 0) {
      toast("err", "Площа має бути більше 0");
      return;
    }

    if (!Number.isFinite(fieldGroupId) || fieldGroupId <= 0) {
      toast("err", "Оберіть групу для поля");
      return;
    }

    if (!isPolygonFeature(polyFeature)) {
      toast("err", "Спочатку обведи поле Polygon на мапі");
      return;
    }

    const payload = {
      name,
      areaHa: area,
      note: note || null,
      fieldGroupId,
      geometry: polyFeature.geometry,
    };

    setCreatingField(true);
    try {
      const res = await apiFetch(
        user,
        "/api/fields",
        { method: "POST", body: JSON.stringify(payload) },
        navigate
      );

      if (!res.ok) {
        const t = await safeReadError(res);
        toast("err", t || `Не вдалося створити поле (${res.status})`);
        return;
      }

      setFieldCreateOpen(false);
      toast("ok", "Поле створено ✅");
      await load();
      setViewMode("fields");
      setGroupFilter(String(fieldGroupId));
    } finally {
      setCreatingField(false);
    }
  }

  function openEditGroup(e, g) {
    e.stopPropagation();
    setEditGroup(g);
    setEditGroupName(g.name || "");
    setEditGroupNote(g.note || "");
    setEditGroupOpen(true);
  }

  async function handleEditGroup(ev) {
    ev.preventDefault();
    if (!user || savingGroup || !editGroup) return;
    const name = editGroupName.trim();
    const note = editGroupNote.trim();
    if (!name) { toast("err", "Введи назву групи"); return; }
    setSavingGroup(true);
    try {
      const res = await apiFetch(user, `/api/field-groups/${editGroup.id}`, {
        method: "PATCH",
        body: JSON.stringify({ name, note: note || null }),
      });
      if (!res.ok) {
        const t = await safeReadError(res);
        toast("err", t || `Не вдалося оновити групу (${res.status})`);
        return;
      }
      setEditGroupOpen(false);
      toast("ok", "Групу оновлено ✅");
      await load();
    } finally {
      setSavingGroup(false);
    }
  }

  async function handleDeleteGroup() {
    if (!user || deletingGroup || !confirmDeleteGroup) return;
    setDeletingGroup(true);
    try {
      const res = await apiFetch(user, `/api/field-groups/${confirmDeleteGroup.id}`, { method: "DELETE" });
      if (!res.ok) {
        const t = await safeReadError(res);
        toast("err", t || `Не вдалося видалити групу (${res.status})`);
        setConfirmDeleteGroup(null);
        return;
      }
      toast("ok", "Групу видалено 🗑");
      if (groupFilter === String(confirmDeleteGroup.id)) setGroupFilter("all");
      setConfirmDeleteGroup(null);
      await load();
    } finally {
      setDeletingGroup(false);
    }
  }

  const right = (
    <div className="fg-topActions">
      {viewMode === "groups" ? (
        <button
          type="button"
          className="btn primary"
          onClick={() => {
            setViewMode("fields");
            setGroupFilter("all");
          }}
        >
          ← Всі поля
        </button>
      ) : (
        <button
          type="button"
          className="btn primary"
          onClick={() => setViewMode("groups")}
        >
          ☰ Групи полів
        </button>
      )}

      <button
        type="button"
        className="btn primary"
        onClick={() =>
          openCreateField(
            groupFilter !== "all" && groupFilter !== "none" ? groupFilter : ""
          )
        }
      >
        + Додати поле
      </button>

      <button type="button" className="btn" onClick={openCreateGroup}>
        + Додати групу
      </button>

      <button
        type="button"
        className="btn"
        onClick={() => {
          setQ("");
          setGroupFilter("all");
          setViewMode("fields");
        }}
      >
        ✕ Скинути
      </button>

      <button type="button" className="btn" onClick={() => load()}>
        ⟳ Оновити
      </button>

      <button type="button" className="btn" onClick={() => navigate(-1)}>
        ← Назад
      </button>
    </div>
  );

  return (
    <AppShell
      title={viewMode === "groups" ? "Групи полів" : "Усі поля"}
      right={right}
    >
      <div className="fg-theme">
        <style>{`
          .fg-theme{
            --fg-bg: var(--bg);
            --fg-panel: var(--panel);
            --fg-panel2: var(--panel2);
            --fg-soft: var(--surface-soft);
            --fg-surface: var(--surface);
            --fg-text: var(--text);
            --fg-muted: var(--muted);
            --fg-border: var(--border);
            --fg-border-strong: var(--border-strong);
            --fg-shadow: var(--shadow);
            --fg-shadow-soft: var(--shadow-soft);
            --fg-ok-bg: rgba(34,197,94,0.14);
            --fg-ok-br: rgba(34,197,94,0.36);
            --fg-warn-bg: rgba(251,191,36,0.14);
            --fg-warn-br: rgba(251,191,36,0.34);
            --fg-danger-bg: rgba(239,68,68,0.14);
            --fg-danger-br: rgba(239,68,68,0.34);
            color: var(--fg-text);
          }

          .fg-theme *{
            box-sizing: border-box;
          }

          .fg-topActions{
            display:flex;
            gap:10px;
            flex-wrap:wrap;
            width:100%;
          }

          .fg-toast{
            position: fixed;
            right: 16px;
            top: 16px;
            z-index: 999;
            padding: 11px 14px;
            border-radius: 16px;
            border: 1px solid var(--fg-border);
            background: var(--fg-panel2);
            box-shadow: var(--fg-shadow);
            backdrop-filter: blur(12px);
            max-width: min(420px, calc(100vw - 24px));
          }

          .fg-panel{
            margin-top: 14px;
            padding: 16px;
          }

          .fg-head{
            display:flex;
            justify-content:space-between;
            align-items:flex-start;
            gap:12px;
            flex-wrap:wrap;
          }

          .fg-headLeft{
            display:flex;
            flex-direction:column;
            gap:4px;
            min-width:0;
          }

          .fg-title{
            font-size: clamp(20px, 2vw, 26px);
            font-weight: 1000;
            letter-spacing: .01em;
            color: var(--fg-text);
            line-height: 1.15;
            word-break: break-word;
          }

          .fg-sub{
            color: var(--fg-muted);
            font-size: 13px;
            font-weight: 700;
            line-height: 1.45;
          }

          .fg-stats{
            margin-top: 14px;
            display:grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 12px;
          }

          .fg-statCard{
            border-radius: 18px;
            border: 1px solid var(--fg-border);
            background: linear-gradient(180deg, var(--fg-panel2), var(--fg-panel));
            box-shadow: var(--fg-shadow-soft);
            padding: 14px 16px;
            position: relative;
            overflow: hidden;
            min-width: 0;
          }

          .fg-statCard::before{
            content: "";
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: 4px;
            background: rgba(34,197,94,0.45);
          }

          .fg-statLabel{
            font-size: 12px;
            font-weight: 900;
            color: var(--fg-muted);
            text-transform: uppercase;
            letter-spacing: .04em;
            line-height: 1.35;
          }

          .fg-statValue{
            margin-top: 8px;
            font-size: clamp(20px, 2vw, 22px);
            font-weight: 1000;
            color: var(--fg-text);
            line-height: 1.1;
            word-break: break-word;
          }

          .fg-toolbar{
            margin-top: 16px;
            display:grid;
            grid-template-columns: 1.2fr .8fr;
            gap: 12px;
          }

          .fg-searchBox,
          .fg-filterBox{
            border-radius: 20px;
            border: 1px solid var(--fg-border);
            background: var(--fg-panel);
            box-shadow: var(--fg-shadow-soft);
            padding: 14px;
            min-width: 0;
          }

          .fg-boxTitle{
            font-size: 13px;
            font-weight: 950;
            color: var(--fg-muted);
            text-transform: uppercase;
            letter-spacing: .04em;
            line-height: 1.35;
          }

          .fg-searchRow{
            margin-top: 10px;
            display:flex;
            gap:10px;
            align-items:center;
            flex-wrap:wrap;
          }

          .fg-searchInput,
          .fg-input,
          .fg-select,
          .fg-textarea{
            width: 100%;
            min-height: 46px;
            border-radius: 16px;
            border: 1px solid var(--fg-border);
            background: var(--fg-surface);
            color: var(--fg-text);
            padding: 12px 14px;
            outline: none;
            font-weight: 800;
            transition: border .15s ease, box-shadow .15s ease, background .15s ease;
            font-size: 15px;
          }

          .fg-textarea{
            min-height: 110px;
            resize: vertical;
            font-family: inherit;
            line-height: 1.45;
          }

          .fg-searchInput::placeholder,
          .fg-input::placeholder,
          .fg-textarea::placeholder{
            color: var(--fg-muted);
          }

          .fg-searchInput:focus,
          .fg-input:focus,
          .fg-select:focus,
          .fg-textarea:focus{
            border-color: var(--fg-ok-br);
            box-shadow: 0 0 0 3px rgba(34,197,94,0.10);
          }

          .fg-clearBtn{
            height: 46px;
            min-width: 46px;
            padding: 0 14px;
            border-radius: 14px;
            border: 1px solid var(--fg-border);
            background: var(--fg-panel2);
            color: var(--fg-text);
            font-weight: 950;
            cursor: pointer;
            flex: 0 0 auto;
          }

          .fg-filterChips{
            margin-top: 14px;
            display:flex;
            gap:10px;
            flex-wrap:wrap;
          }

          .fg-chip{
            display:inline-flex;
            align-items:center;
            gap:8px;
            min-height: 42px;
            padding: 10px 14px;
            border-radius: 999px;
            border: 1px solid var(--fg-border);
            background: var(--fg-panel2);
            color: var(--fg-text);
            font-weight: 900;
            cursor: pointer;
            transition: transform .12s ease, border .12s ease, background .12s ease;
            max-width: 100%;
          }

          .fg-chip:hover{
            transform: translateY(-1px);
            border-color: var(--fg-border-strong);
          }

          .fg-chip.active{
            background: var(--fg-ok-bg);
            border-color: var(--fg-ok-br);
          }

          .fg-chip--nav{
            border-color: var(--fg-muted);
            font-weight: 950;
            gap: 5px;
          }
          .fg-chip--nav:hover{
            border-color: var(--fg-text);
            background: var(--fg-surface);
          }

          .fg-chipCount{
            opacity: .75;
            font-size: 12px;
            font-weight: 950;
          }

          .fg-list{
            margin-top: 16px;
            display:grid;
            grid-template-columns: 1fr;
            gap: 14px;
          }

          .fg-rowBtn{
            appearance: none;
            border: 0;
            background: transparent;
            padding: 0;
            margin: 0;
            width: 100%;
            text-align: left;
            cursor: pointer;
            font: inherit;
          }

          .fg-rowBtn:focus{
            outline: none;
          }

          .fg-card,
          .fg-groupCard{
            border-radius: 22px;
            border: 1px solid var(--fg-border);
            background: linear-gradient(180deg, var(--fg-panel2), var(--fg-panel));
            box-shadow: var(--fg-shadow-soft);
            overflow: hidden;
            transition: transform .12s ease, border .12s ease, box-shadow .12s ease;
            position: relative;
          }

          .fg-rowBtn:hover .fg-card,
          .fg-rowBtn:hover .fg-groupCard{
            transform: translateY(-2px);
            border-color: var(--fg-border-strong);
            box-shadow: var(--fg-shadow);
          }

          .fg-rowBtn:active .fg-card,
          .fg-rowBtn:active .fg-groupCard{
            transform: scale(.996);
          }

          .fg-cardInner{
            padding: 16px;
          }

          .fg-cardTop{
            display:flex;
            justify-content:space-between;
            align-items:flex-start;
            gap: 14px;
            flex-wrap:wrap;
          }

          .fg-cardLeft{
            min-width: 0;
            flex: 1 1 420px;
          }

          .fg-name{
            font-size: clamp(17px, 1.8vw, 19px);
            font-weight: 1000;
            letter-spacing: .01em;
            color: var(--fg-text);
            line-height: 1.15;
            word-break: break-word;
          }

          .fg-id{
            margin-top: 4px;
            font-size: 13px;
            color: var(--fg-muted);
            font-weight: 800;
            word-break: break-word;
          }

          .fg-meta{
            display:flex;
            gap: 10px;
            flex-wrap:wrap;
            align-items:center;
            justify-content:flex-end;
            max-width: 100%;
          }

          .fg-badge{
            display:inline-flex;
            align-items:center;
            justify-content:center;
            gap:8px;
            min-height: 38px;
            padding: 8px 12px;
            border-radius: 999px;
            border: 1px solid var(--fg-border);
            background: var(--fg-panel2);
            color: var(--fg-text);
            font-size: 13px;
            font-weight: 950;
            white-space: normal;
            line-height: 1.25;
            text-align: center;
            max-width: 100%;
          }

          .fg-badge.ok{
            background: var(--fg-ok-bg);
            border-color: var(--fg-ok-br);
          }

          .fg-badge.warn{
            background: var(--fg-warn-bg);
            border-color: var(--fg-warn-br);
          }

          .fg-layout{
            margin-top: 14px;
            display:grid;
            grid-template-columns: 280px minmax(0, 1fr) 280px;
            gap: 12px;
            align-items:start;
          }

          .fg-sideBox,
          .fg-mainBox{
            border-radius: 18px;
            border: 1px solid var(--fg-border);
            background: var(--fg-soft);
            padding: 13px;
            min-width: 0;
          }

          .fg-sectionK{
            font-size: 12px;
            font-weight: 950;
            color: var(--fg-muted);
            text-transform: uppercase;
            letter-spacing: .04em;
            margin-bottom: 8px;
            line-height: 1.35;
          }

          .fg-note{
            color: var(--fg-text);
            line-height: 1.5;
            font-weight: 700;
            white-space: pre-wrap;
            word-break: break-word;
          }

          .fg-noteEmpty{
            color: var(--fg-muted);
            font-weight: 700;
          }

          .fg-kvGrid{
            display:grid;
            grid-template-columns: 1fr;
            gap: 10px;
          }

          .fg-kv{
            border-radius: 16px;
            border: 1px solid var(--fg-border);
            background: var(--fg-panel);
            padding: 11px 12px;
            display:flex;
            justify-content:space-between;
            gap:10px;
            align-items:center;
          }

          .fg-k{
            font-size: 12px;
            font-weight: 950;
            color: var(--fg-muted);
            text-transform: uppercase;
            letter-spacing: .03em;
            line-height: 1.35;
          }

          .fg-v{
            font-size: 14px;
            font-weight: 950;
            color: var(--fg-text);
            text-align: right;
            word-break: break-word;
          }

          .fg-geoCard{
            border-radius: 18px;
            border: 1px solid var(--fg-border);
            background: var(--fg-panel);
            overflow: hidden;
          }

          .fg-geoHead{
            padding: 12px 13px;
            border-bottom: 1px solid var(--fg-border);
            display:flex;
            justify-content:space-between;
            align-items:center;
            gap:10px;
            flex-wrap:wrap;
          }

          .fg-geoTitle{
            font-size: 12px;
            font-weight: 950;
            color: var(--fg-muted);
            text-transform: uppercase;
            letter-spacing: .04em;
          }

          .fg-geoStatus{
            display:inline-flex;
            align-items:center;
            gap:8px;
            padding: 6px 10px;
            border-radius: 999px;
            font-size: 12px;
            font-weight: 950;
            border: 1px solid var(--fg-border);
            background: var(--fg-panel2);
            color: var(--fg-text);
            white-space: normal;
            line-height: 1.25;
            text-align: center;
          }

          .fg-geoStatus.ok{
            background: var(--fg-ok-bg);
            border-color: var(--fg-ok-br);
          }

          .fg-geoStatus.warn{
            background: var(--fg-warn-bg);
            border-color: var(--fg-warn-br);
          }

          .fg-geoCanvas{
            padding: 12px;
          }

          .fg-geoSvg{
            width: 100%;
            height: 170px;
            display: block;
            border-radius: 14px;
            overflow: hidden;
            background: var(--fg-panel2);
            border: 1px solid var(--fg-border);
          }

          .fg-geoBg{
            fill: rgba(255,255,255,0.02);
          }

          .fg-geoGridLine{
            stroke: rgba(127,127,127,0.14);
            stroke-width: 1;
          }

          .fg-geoShapeFill{
            fill: rgba(34,197,94,0.20);
          }

          .fg-geoShapeStroke{
            fill: none;
            stroke: rgba(34,197,94,0.92);
            stroke-width: 2.2;
            vector-effect: non-scaling-stroke;
            stroke-linejoin: round;
            stroke-linecap: round;
          }

          /* ---- Field thumbnail (shown only on mobile) ---- */
          .fg-fieldThumb{
            display: none;
            flex: 0 0 auto;
            width: 64px;
            height: 64px;
            border-radius: 14px;
            overflow: hidden;
            border: 1px solid var(--fg-border);
            background: var(--fg-panel2);
            align-items: center;
            justify-content: center;
          }

          .fg-fieldThumb--empty{
            border-style: dashed;
          }

          .fg-thumbSvg{
            width: 100%;
            height: 100%;
            display: block;
          }

          .fg-geoEmpty{
            height: 170px;
            display:flex;
            flex-direction:column;
            align-items:center;
            justify-content:center;
            gap:8px;
            border-radius: 14px;
            border: 1px dashed var(--fg-border-strong);
            background: var(--fg-panel2);
            color: var(--fg-muted);
            margin: 12px;
          }

          .fg-geoEmptyIcon{
            font-size: 24px;
            opacity: .8;
          }

          .fg-geoEmptyText{
            font-size: 13px;
            font-weight: 800;
            text-align: center;
            padding: 0 10px;
          }

          .fg-geoInfo{
            padding: 0 12px 12px 12px;
            color: var(--fg-text);
            font-size: 13px;
            line-height: 1.45;
            font-weight: 750;
            word-break: break-word;
          }

          .fg-footer{
            margin-top: 14px;
            display:flex;
            align-items:center;
            justify-content:space-between;
            gap:10px;
            flex-wrap:wrap;
          }

          .fg-openText{
            color: var(--fg-text);
            font-weight: 950;
          }

          .fg-helper{
            color: var(--fg-muted);
            font-size: 13px;
            font-weight: 800;
            line-height: 1.4;
          }

          .fg-empty{
            margin-top: 16px;
            padding: 18px 16px;
            border-radius: 18px;
            border: 1px dashed var(--fg-border-strong);
            background: var(--fg-soft);
            color: var(--fg-muted);
            font-weight: 850;
            line-height: 1.45;
          }

          .fg-modal{
            position: fixed;
            inset: 0;
            background: rgba(0,0,0,0.44);
            backdrop-filter: blur(8px);
            display:flex;
            align-items:center;
            justify-content:center;
            z-index: 5000;
            padding: 16px;
          }

          .fg-modalCard{
            width: min(1000px, 96vw);
            max-height: calc(100vh - 32px);
            overflow: auto;
            border-radius: 22px;
            border: 1px solid var(--fg-border);
            background: var(--modal-bg);
            color: var(--fg-text);
            box-shadow: var(--fg-shadow);
          }

          .fg-modalHead{
            padding: 14px 14px 12px 14px;
            border-bottom: 1px solid var(--fg-border);
            display:flex;
            align-items:flex-start;
            justify-content:space-between;
            gap:12px;
            background: linear-gradient(180deg, rgba(34,197,94,0.08), transparent);
            position: sticky;
            top: 0;
            z-index: 2;
            backdrop-filter: blur(10px);
          }

          .fg-modalTitle{
            font-size: clamp(18px, 2vw, 22px);
            font-weight: 1000;
            color: var(--fg-text);
            line-height: 1.15;
          }

          .fg-modalSub{
            margin-top: 2px;
            color: var(--fg-muted);
            font-size: 13px;
            font-weight: 700;
            line-height: 1.45;
          }

          .fg-modalBody{
            padding: 14px;
          }

          .fg-formGrid{
            display:grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
          }

          .fg-formGroup{
            display:flex;
            flex-direction:column;
            gap:8px;
            min-width: 0;
          }

          .fg-label{
            font-size: 12px;
            font-weight: 950;
            color: var(--fg-muted);
            text-transform: uppercase;
            letter-spacing: .04em;
            line-height: 1.35;
          }

          .fg-hint{
            margin-top: 6px;
            color: var(--fg-muted);
            font-size: 12px;
            line-height: 1.4;
            font-weight: 700;
          }

          .fg-stickyActions{
            margin-top: 14px;
            display:flex;
            gap:10px;
            justify-content:flex-end;
            flex-wrap:wrap;
          }

          .fg-groupGrid{
            margin-top: 16px;
            display:grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 14px;
            align-items: stretch;
          }

          .fg-groupGrid .fg-rowBtn{
            height: 100%;
            display: flex;
          }

          .fg-groupGrid .fg-groupCard{
            flex: 1;
            display: flex;
            flex-direction: column;
          }

          .fg-groupGrid .fg-groupInner{
            flex: 1;
            display: flex;
            flex-direction: column;
          }

          .fg-groupGrid .fg-groupStats{
            margin-top: auto;
            padding-top: 12px;
          }

          .fg-groupTop{
            display:flex;
            align-items:flex-start;
            justify-content:space-between;
            gap: 12px;
            flex-wrap:wrap;
          }

          .fg-groupName{
            font-size: clamp(18px, 2vw, 20px);
            font-weight: 1000;
            line-height: 1.15;
            color: var(--fg-text);
            word-break: break-word;
          }

          .fg-groupNote{
            margin-top: 10px;
            color: var(--fg-muted);
            font-size: 14px;
            line-height: 1.45;
            font-weight: 750;
            min-height: 42px;
            word-break: break-word;
          }

          .fg-groupStats{
            margin-top: 14px;
            display:grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 10px;
          }

          .fg-groupStat{
            border-radius: 16px;
            border: 1px solid var(--fg-border);
            background: var(--fg-soft);
            padding: 12px;
            min-width: 0;
          }

          .fg-groupStatK{
            font-size: 12px;
            color: var(--fg-muted);
            font-weight: 900;
            text-transform: uppercase;
            letter-spacing: .04em;
            line-height: 1.35;
          }

          .fg-groupStatV{
            margin-top: 6px;
            font-size: 18px;
            font-weight: 1000;
            color: var(--fg-text);
            word-break: break-word;
          }

          .fg-groupFooter{
            margin-top: 14px;
            display:flex;
            justify-content:space-between;
            align-items:center;
            gap: 10px;
            flex-wrap:wrap;
          }

          .fg-mapBox{
            margin-top: 12px;
            border-radius: 18px;
            border: 1px solid var(--fg-border);
            overflow: hidden;
            background: var(--fg-panel);
            min-width: 0;
          }

          .fg-mapBoxTitle{
            padding: 12px 14px;
            border-bottom: 1px solid var(--fg-border);
            font-size: 12px;
            font-weight: 950;
            color: var(--fg-muted);
            text-transform: uppercase;
            letter-spacing: .04em;
            line-height: 1.35;
          }

          .fg-createGrid{
            display:grid;
            grid-template-columns: 360px minmax(0, 1fr);
            gap: 14px;
            align-items:start;
          }

          .fg-createSide{
            display:flex;
            flex-direction:column;
            gap: 12px;
            min-width: 0;
          }

          .fg-miniStatus{
            margin-top: 4px;
            padding: 12px 14px;
            border-radius: 16px;
            border: 1px solid var(--fg-border);
            background: var(--fg-soft);
            font-size: 14px;
            font-weight: 800;
            line-height: 1.45;
          }

          @media (max-width: 1100px){
            .fg-layout{
              grid-template-columns: 1fr;
            }
          }

          @media (max-width: 980px){
            .fg-stats{
              grid-template-columns: repeat(2, minmax(0, 1fr));
            }

            .fg-createGrid{
              grid-template-columns: 1fr;
            }
          }

          @media (max-width: 900px){
            .fg-toolbar{
              grid-template-columns: 1fr;
            }

            .fg-groupGrid{
              grid-template-columns: 1fr;
            }
          }

          @media (max-width: 820px){
            .fg-formGrid{
              grid-template-columns: 1fr;
            }
          }

          @media (max-width: 560px){
            .fg-stats,
            .fg-groupStats{
              grid-template-columns: 1fr;
            }

            .fg-panel{
              padding: 12px;
            }

            .fg-cardInner,
            .fg-groupInner{
              padding: 12px;
            }

            .fg-searchRow,
            .fg-footer,
            .fg-groupFooter,
            .fg-groupTop,
            .fg-cardTop,
            .fg-head,
            .fg-stickyActions,
            .fg-modalHead{
              flex-direction: column;
              align-items: stretch;
            }

            .fg-meta{
              justify-content:flex-start;
            }

            .fg-badge{
              width: 100%;
              justify-content: flex-start;
              border-radius: 14px;
            }

            .fg-clearBtn,
            .fg-stickyActions .btn,
            .fg-topActions .btn,
            .fg-modalHead .btn{
              width: 100%;
            }

            .fg-modal{
              padding: 0;
              align-items: stretch;
              justify-content: stretch;
            }

            .fg-modalCard{
              width: 100vw;
              max-height: 100vh;
              min-height: 100vh;
              border-radius: 0;
              border-left: 0;
              border-right: 0;
            }

            .fg-modalBody{
              padding: 12px;
            }

            .fg-searchBox,
            .fg-filterBox{
              padding: 12px;
              border-radius: 16px;
            }

            .fg-card,
            .fg-groupCard{
              border-radius: 18px;
            }

            .fg-openText{
              white-space: normal;
            }
          }

          .fg-iconBtn{
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 36px;
            height: 36px;
            border-radius: 10px;
            border: 1px solid var(--fg-border);
            background: var(--fg-panel2);
            color: var(--fg-text);
            cursor: pointer;
            font-size: 15px;
            transition: background .12s, border-color .12s, transform .1s;
            flex-shrink: 0;
          }
          .fg-iconBtn:hover{
            background: var(--fg-surface);
            border-color: var(--fg-border-strong);
            transform: translateY(-1px);
          }
          .fg-iconBtn:active{ transform: scale(.94); }
          .fg-iconBtn--danger:hover{
            background: var(--fg-danger-bg);
            border-color: var(--fg-danger-br);
          }

          .fg-confirmOverlay{
            position: fixed;
            inset: 0;
            background: rgba(0,0,0,0.52);
            backdrop-filter: blur(8px);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 6000;
            padding: 16px;
          }
          .fg-confirmCard{
            width: min(360px, 96vw);
            border-radius: 22px;
            border: 1px solid var(--fg-border);
            background: var(--modal-bg);
            box-shadow: var(--fg-shadow);
            padding: 28px 24px 24px;
            text-align: center;
            color: var(--fg-text);
          }
          .fg-confirmIcon{ font-size: 36px; margin-bottom: 12px; }
          .fg-confirmTitle{ font-size: 18px; font-weight: 1000; margin-bottom: 6px; }
          .fg-confirmSub{ font-size: 14px; color: var(--fg-muted); font-weight: 700; margin-bottom: 22px; line-height: 1.45; }
          .fg-confirmBtns{ display: flex; gap: 10px; justify-content: center; }
          .fg-confirmBtns .btn{ flex: 1; min-height: 46px; font-size: 14px; }

          @media (max-width: 560px){
            .fg-confirmBtns{ flex-direction: column-reverse; }
            .fg-iconBtn{ width: 44px; height: 44px; }
          }

          @media (max-width: 380px){
            .fg-input,
            .fg-select,
            .fg-searchInput,
            .fg-textarea{
              font-size: 14px;
              padding: 11px 12px;
            }

            .fg-name,
            .fg-groupName{
              font-size: 16px;
            }

            .fg-statValue{
              font-size: 20px;
            }

            .fg-badge{
              font-size: 12px;
              padding: 8px 10px;
            }
          }

          /* ---- Mobile: compact cards with shape thumbnail ---- */
          @media (max-width: 760px) {
            /* Hide the heavy 3-column info grid */
            .fg-layout {
              display: none;
            }

            /* Show field shape thumbnail */
            .fg-fieldThumb {
              display: flex;
            }

            /* Card: left accent bar */
            .fg-card::before {
              content: "";
              position: absolute;
              left: 0; top: 0; bottom: 0;
              width: 3px;
              background: rgba(34,197,94,0.55);
              border-radius: 22px 0 0 22px;
            }

            /* Card top: thumbnail | name | badges */
            .fg-cardInner {
              padding: 14px;
            }
            .fg-cardTop {
              flex-wrap: nowrap;
              align-items: center;
              gap: 10px;
            }
            .fg-cardLeft {
              flex: 1 1 auto;
              min-width: 0;
            }
            .fg-name {
              font-size: 16px;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .fg-id {
              font-size: 12px;
              margin-top: 3px;
            }
            .fg-meta {
              flex-direction: column;
              align-items: flex-end;
              flex-shrink: 0;
              gap: 5px;
            }
            .fg-badge {
              min-height: 32px;
              font-size: 12px;
              border-radius: 10px;
              padding: 5px 10px;
              white-space: nowrap;
            }

            /* Footer with separator */
            .fg-footer {
              margin-top: 10px;
              padding-top: 10px;
              border-top: 1px solid var(--fg-border);
              flex-direction: row;
              align-items: center;
              justify-content: flex-end;
            }
            .fg-openText {
              font-size: 14px;
              font-weight: 950;
            }

            /* Group cards: compact */
            .fg-groupStats {
              grid-template-columns: 1fr 1fr;
            }
            .fg-groupStatV {
              font-size: 20px;
            }
            .fg-groupInner {
              padding: 14px;
            }
            .fg-groupName {
              font-size: 18px;
            }
            .fg-groupNote {
              min-height: auto;
              margin-top: 8px;
            }
            .fg-groupFooter {
              flex-direction: row;
              align-items: center;
              justify-content: flex-end;
              margin-top: 10px;
              padding-top: 10px;
              border-top: 1px solid var(--fg-border);
            }

            /* Touch-friendly inputs */
            .fg-input,
            .fg-select,
            .fg-searchInput,
            .fg-textarea {
              font-size: 16px;
              min-height: 48px;
            }
            .fg-btn, .btn {
              min-height: 44px;
            }

            /* Top action buttons */
            .fg-topActions {
              flex-wrap: wrap;
            }
            .fg-topActions .btn {
              flex: 1;
              min-width: 140px;
            }

            /* Modal centered on mobile */
            .fg-modal {
              padding: 16px;
              align-items: center;
              justify-content: center;
            }
            .fg-modalCard {
              width: 100% !important;
              max-height: 90vh !important;
              min-height: auto !important;
              border-radius: 18px !important;
            }
          }
        `}</style>

        {msg && (
          <div
            className="fg-toast"
            style={{
              borderColor:
                msg.type === "ok" ? "var(--fg-ok-br)" : "var(--fg-danger-br)",
            }}
          >
            <span style={{ fontWeight: 950 }}>{msg.text}</span>
          </div>
        )}

        <div className="panel fg-panel">
          <div className="fg-head">
            <div className="fg-headLeft">
              <div className="fg-title">
                {viewMode === "groups"
                  ? "Список груп полів"
                  : groupFilter === "all"
                  ? "Список полів"
                  : `Поля • ${activeGroupTitle}`}
              </div>
            </div>
          </div>

          <div className="fg-stats">
            <div className="fg-statCard">
              <div className="fg-statLabel">Усього полів</div>
              <div className="fg-statValue">{totals.totalFields}</div>
            </div>

            <div className="fg-statCard">
              <div className="fg-statLabel">
                {viewMode === "groups" ? "Усього груп" : "Показано"}
              </div>
              <div className="fg-statValue">
                {viewMode === "groups" ? groups.length : totals.visibleFields}
              </div>
            </div>

            <div className="fg-statCard">
              <div className="fg-statLabel">
                {viewMode === "groups" ? "Поля без групи" : "Площа видимих"}
              </div>
              <div className="fg-statValue">
                {viewMode === "groups"
                  ? groupStats.get("none")?.count || 0
                  : `${formatArea(totals.visibleArea)} ha`}
              </div>
            </div>

            <div className="fg-statCard">
              <div className="fg-statLabel">З геометрією</div>
              <div className="fg-statValue">{totals.contourCount}</div>
            </div>
          </div>

          <div className="fg-toolbar">
            <div className="fg-searchBox">
              <div className="fg-searchRow">
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="fg-searchInput"
                  placeholder={
                    viewMode === "groups"
                      ? "Назва групи, ID, нотатка..."
                      : "Назва, ID, група, нотатка, polygon..."
                  }
                />
                {q ? (
                  <button
                    type="button"
                    className="fg-clearBtn"
                    onClick={() => setQ("")}
                    title="Очистити пошук"
                  >
                    ✕
                  </button>
                ) : null}
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              {viewMode === "groups" ? (
                <button
                  type="button"
                  className="btn"
                  onClick={() => { setViewMode("fields"); setGroupFilter("all"); }}
                >
                  ← Всі поля
                </button>
              ) : (
                <button
                  type="button"
                  className="btn"
                  onClick={() => openCreateField(groupFilter !== "all" && groupFilter !== "none" ? groupFilter : "")}
                >
                  + Додати поле
                </button>
              )}
              <button type="button" className="btn" onClick={openCreateGroup}>
                + Нова група
              </button>
            </div>
          </div>

          {viewMode === "fields" && (
            <div className="fg-filterChips">
              <button
                type="button"
                className={`fg-chip ${groupFilter === "all" ? "active" : ""}`}
                onClick={() => setGroupFilter("all")}
              >
                Усі поля
                <span className="fg-chipCount">({fields.length})</span>
              </button>

              <button
                type="button"
                className={`fg-chip ${groupFilter === "none" ? "active" : ""}`}
                onClick={() => setGroupFilter("none")}
              >
                Без групи
                <span className="fg-chipCount">
                  ({groupStats.get("none")?.count || 0})
                </span>
              </button>

              <button
                type="button"
                className="fg-chip fg-chip--nav"
                onClick={() => setViewMode("groups")}
              >
                🗂 Групи полів
                <span className="fg-chipCount">({groups.length})</span>
              </button>

              {groups.map((g) => {
                const stats = groupStats.get(String(g.id));
                return (
                  <button
                    key={g.id}
                    type="button"
                    className={`fg-chip ${
                      String(groupFilter) === String(g.id) ? "active" : ""
                    }`}
                    onClick={() => setGroupFilter(String(g.id))}
                  >
                    {g.name || `Група #${g.id}`}
                    <span className="fg-chipCount">({stats?.count || 0})</span>
                  </button>
                );
              })}
            </div>
          )}

          {loading ? (
            <div className="fg-empty">Завантажую...</div>
          ) : viewMode === "groups" ? (
            filteredGroups.length === 0 ? (
              <div className="fg-empty">
                {groups.length === 0
                  ? "Груп поки немає."
                  : "Нічого не знайдено по поточному пошуку."}
              </div>
            ) : (
              <div className="fg-groupGrid">
                {filteredGroups.map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    className="fg-rowBtn"
                    onClick={() => {
                      setGroupFilter(String(g.id));
                      setViewMode("fields");
                    }}
                  >
                    <div className="fg-groupCard">
                      <div className="fg-groupInner">
                        <div className="fg-groupTop">
                          <div style={{ minWidth: 0 }}>
                            <div className="fg-groupName">{g.name || `Група #${g.id}`}</div>
                            <div className="fg-id">ID: {g.id}</div>
                          </div>

                          <div className="fg-meta">
                            <span className="fg-badge">🗂 Полів: {g.fieldsCount}</span>
                            <span className="fg-badge">📏 {formatArea(g.area)} ha</span>
                          </div>
                        </div>

                        {g.note && (
                          <div className="fg-groupNote">{g.note}</div>
                        )}

                        <div className="fg-groupStats">
                          <div className="fg-groupStat">
                            <div className="fg-groupStatK">Полів</div>
                            <div className="fg-groupStatV">{g.fieldsCount}</div>
                          </div>

                          <div className="fg-groupStat">
                            <div className="fg-groupStatK">Площа</div>
                            <div className="fg-groupStatV">{formatArea(g.area)} ha</div>
                          </div>
                        </div>

                        <div className="fg-groupFooter">
                          <div className="fg-openText">Відкрити групу →</div>
                          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                            <button
                              type="button"
                              className="fg-iconBtn"
                              title="Редагувати групу"
                              onClick={(e) => openEditGroup(e, g)}
                            >✏️</button>
                            <button
                              type="button"
                              className="fg-iconBtn fg-iconBtn--danger"
                              title="Видалити групу"
                              onClick={(e) => { e.stopPropagation(); setConfirmDeleteGroup(g); }}
                            >🗑</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )
          ) : filteredFields.length === 0 ? (
            <div className="fg-empty">
              {fields.length === 0
                ? "Полів поки немає."
                : "Нічого не знайдено по поточному пошуку або фільтру."}
            </div>
          ) : (
            <div className="fg-list">
              {filteredFields.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className="fg-rowBtn"
                  onClick={() => navigate(`/fields/${f.id}`)}
                >
                  <div className="fg-card">
                    <div className="fg-cardInner">
                      <div className="fg-cardTop">
                        <FieldThumb geometry={f.geometry} />
                        <div className="fg-cardLeft">
                          <div className="fg-name">{f.name || `Поле #${f.id}`}</div>
                          <div className="fg-id">ID: {f.id}</div>
                        </div>

                        <div className="fg-meta">
                          <span className="fg-badge">
                            📏 {formatArea(f.area)} ha
                          </span>
                          <span className="fg-badge">
                            🗂 {f.groupName || "Без групи"}
                          </span>
                        </div>
                      </div>

                      <div className="fg-layout">
                        <div className="fg-sideBox">
                          <div className="fg-sectionK">Коротка інформація</div>

                          <div className="fg-kvGrid">
                            <div className="fg-kv">
                              <div className="fg-k">Назва</div>
                              <div className="fg-v">{f.name || "—"}</div>
                            </div>

                            <div className="fg-kv">
                              <div className="fg-k">Група</div>
                              <div className="fg-v">{f.groupName || "Без групи"}</div>
                            </div>

                            <div className="fg-kv">
                              <div className="fg-k">Площа</div>
                              <div className="fg-v">{formatArea(f.area)} ha</div>
                            </div>

                          </div>
                        </div>

                        <div className="fg-mainBox">
                          <div className="fg-geoCard">
                            <div className="fg-geoHead">
                              <div className="fg-geoTitle">Геометрія поля</div>
                            </div>

                            <GeometryPreview geometry={f.geometry} />
                          </div>
                        </div>

                        <div className="fg-sideBox">
                          <div className="fg-sectionK">Нотатка</div>
                          {f.note ? (
                            <div className="fg-note">{f.note}</div>
                          ) : (
                            <div className="fg-noteEmpty">Нотатка відсутня</div>
                          )}
                        </div>
                      </div>

                      <div className="fg-footer">
                        <div className="fg-openText">Відкрити →</div>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {groupCreateOpen && (
          <div
            className="fg-modal"
            role="dialog"
            aria-modal="true"
            onClick={() => !creatingGroup && setGroupCreateOpen(false)}
          >
            <div className="fg-modalCard" onClick={(e) => e.stopPropagation()}>
              <div className="fg-modalHead">
                <div style={{ minWidth: 0 }}>
                  <div className="fg-modalTitle">Додати групу полів</div>
                </div>
                <button
                  type="button"
                  className="btn"
                  onClick={() => !creatingGroup && setGroupCreateOpen(false)}
                >
                  ✕
                </button>
              </div>

              <form className="fg-modalBody" onSubmit={handleCreateGroup}>
                <div className="fg-formGroup">
                  <div className="fg-label">Назва групи</div>
                  <input
                    className="fg-input"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    placeholder="Напр: Південні поля"
                    disabled={creatingGroup}
                  />
                </div>

                <div className="fg-formGroup" style={{ marginTop: 12 }}>
                  <div className="fg-label">Нотатка</div>
                  <textarea
                    className="fg-textarea"
                    value={newGroupNote}
                    onChange={(e) => setNewGroupNote(e.target.value)}
                    placeholder="Короткий опис групи"
                    disabled={creatingGroup}
                  />
                </div>

                <div className="fg-stickyActions">
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setGroupCreateOpen(false)}
                    disabled={creatingGroup}
                  >
                    Скасувати
                  </button>
                  <button type="submit" className="btn primary" disabled={creatingGroup}>
                    {creatingGroup ? "Створюю..." : "Створити групу"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {editGroupOpen && editGroup && (
          <div
            className="fg-modal"
            role="dialog"
            aria-modal="true"
            onClick={() => !savingGroup && setEditGroupOpen(false)}
          >
            <div className="fg-modalCard" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
              <div className="fg-modalHead">
                <div style={{ minWidth: 0 }}>
                  <div className="fg-modalTitle">Редагувати групу</div>
                  <div className="fg-modalSub">{editGroup.name}</div>
                </div>
                <button
                  type="button"
                  className="btn"
                  onClick={() => !savingGroup && setEditGroupOpen(false)}
                >✕</button>
              </div>

              <form className="fg-modalBody" onSubmit={handleEditGroup}>
                <div className="fg-formGroup">
                  <div className="fg-label">Назва групи <span style={{ color: "#ef4444" }}>*</span></div>
                  <input
                    className="fg-input"
                    value={editGroupName}
                    onChange={(e) => setEditGroupName(e.target.value)}
                    placeholder="Напр: Південні поля"
                    disabled={savingGroup}
                    maxLength={25}
                  />
                  <div className="fg-hint">{editGroupName.trim().length}/25 символів</div>
                </div>

                <div className="fg-formGroup" style={{ marginTop: 12 }}>
                  <div className="fg-label">Нотатка</div>
                  <textarea
                    className="fg-textarea"
                    value={editGroupNote}
                    onChange={(e) => setEditGroupNote(e.target.value)}
                    placeholder="Короткий опис групи"
                    disabled={savingGroup}
                    maxLength={150}
                    style={{ minHeight: 90 }}
                  />
                  <div className="fg-hint">{editGroupNote.length}/150 символів</div>
                </div>

                <div className="fg-stickyActions">
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setEditGroupOpen(false)}
                    disabled={savingGroup}
                  >Скасувати</button>
                  <button type="submit" className="btn primary" disabled={savingGroup}>
                    {savingGroup ? "Зберігаю..." : "Зберегти зміни"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {confirmDeleteGroup && (
          <div
            className="fg-confirmOverlay"
            onClick={() => !deletingGroup && setConfirmDeleteGroup(null)}
          >
            <div className="fg-confirmCard" onClick={(e) => e.stopPropagation()}>
              <div className="fg-confirmIcon">🗑️</div>
              <div className="fg-confirmTitle">Видалити групу?</div>
              <div className="fg-confirmSub">
                «{confirmDeleteGroup.name}»<br />
                Разом з групою будуть видалені всі поля в ній. Цю дію не можна скасувати.
              </div>
              <div className="fg-confirmBtns">
                <button
                  type="button"
                  className="btn"
                  onClick={() => setConfirmDeleteGroup(null)}
                  disabled={deletingGroup}
                >Скасувати</button>
                <button
                  type="button"
                  className="btn"
                  style={{ background: "#dc2626", color: "#fff", border: "none", fontWeight: 800 }}
                  onClick={handleDeleteGroup}
                  disabled={deletingGroup}
                >{deletingGroup ? "Видаляю..." : "Так, видалити"}</button>
              </div>
            </div>
          </div>
        )}

        {fieldCreateOpen && (
          <div
            className="fg-modal"
            role="dialog"
            aria-modal="true"
            onClick={() => !creatingField && setFieldCreateOpen(false)}
          >
            <div className="fg-modalCard" onClick={(e) => e.stopPropagation()}>
              <div className="fg-modalHead">
                <div style={{ minWidth: 0 }}>
                  <div className="fg-modalTitle">Додати поле</div>
                </div>
                <button
                  type="button"
                  className="btn"
                  onClick={() => !creatingField && setFieldCreateOpen(false)}
                >
                  ✕
                </button>
              </div>

              <form className="fg-modalBody" onSubmit={handleCreateField}>
                <div className="fg-createGrid">
                  <div className="fg-createSide">
                    <div className="fg-formGroup">
                      <div className="fg-label">Назва поля</div>
                      <input
                        className="fg-input"
                        value={newFieldName}
                        onChange={(e) => setNewFieldName(e.target.value)}
                        placeholder="Напр: Поле 12"
                        disabled={creatingField}
                      />
                    </div>

                    <div className="fg-formGroup">
                      <div className="fg-label">Площа (ha)</div>
                      <input
                        className="fg-input"
                        value={newFieldArea}
                        onChange={(e) => setNewFieldArea(e.target.value)}
                        inputMode="decimal"
                        placeholder="Напр: 15.7"
                        disabled={creatingField}
                      />
                    </div>

                    <div className="fg-formGroup">
                      <div className="fg-label">Група поля</div>
                      <select
                        className="fg-select"
                        value={newFieldGroupId}
                        onChange={(e) => setNewFieldGroupId(e.target.value)}
                        disabled={creatingField}
                      >
                        <option value="">Оберіть групу</option>
                        {groups.map((g) => (
                          <option key={g.id} value={String(g.id)}>
                            {g.name}
                          </option>
                        ))}
                      </select>
                      <div className="fg-hint">
                        Саме до цієї групи буде належати нове поле.
                      </div>
                    </div>

                    <div className="fg-formGroup">
                      <div className="fg-label">Нотатка</div>
                      <textarea
                        className="fg-textarea"
                        value={newFieldNote}
                        onChange={(e) => setNewFieldNote(e.target.value)}
                        placeholder="Коротка нотатка по полю"
                        disabled={creatingField}
                      />
                    </div>

                    <div className="fg-miniStatus">
                      Статус контуру:{" "}
                      {isPolygonFeature(polyFeature) ? (
                        <b style={{ color: "#34d399" }}>✅ Polygon OK</b>
                      ) : (
                        <b style={{ color: "#fb7185" }}>❌ Обведи поле на мапі</b>
                      )}
                    </div>

                    <button
                      type="button"
                      className="btn"
                      onClick={() => setPolyFeature(null)}
                      disabled={creatingField}
                    >
                      Очистити контур 🗑
                    </button>
                  </div>

                  <div className="fg-mapBox">
                    <div className="fg-mapBoxTitle">Контур поля</div>
                    <MapboxFieldDraw
                      height={mapHeight}
                      valueGeoJson={polyFeature}
                      onChangeGeoJson={setPolyFeature}
                      readonly={false}
                    />
                  </div>
                </div>

                <div className="fg-stickyActions">
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setFieldCreateOpen(false)}
                    disabled={creatingField}
                  >
                    Скасувати
                  </button>
                  <button
                    type="submit"
                    className="btn primary"
                    disabled={creatingField || !isPolygonFeature(polyFeature)}
                  >
                    {creatingField ? "Створюю..." : "Створити поле"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}